import { createSelector } from "reselect";

import { isCurrentUserManager, isCurrentUserOwner } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

const getEmailActivityState = (state: RootState) => state.scene.report.emailActivity;

export const getBreakdown = createSelector(getEmailActivityState, ({ breakdown }) => breakdown);
export const getBreakdownView = createSelector(
  getEmailActivityState,
  ({ breakdownView }) => breakdownView
);
export const getDateRange = createSelector(getEmailActivityState, ({ dateRange }) => dateRange);
export const getDateRangeType = createSelector(
  getEmailActivityState,
  ({ dateRangeType }) => dateRangeType
);
export const getEmailActivityScope = createSelector(getEmailActivityState, ({ scope }) => scope);
export const getFeatureRequest = createSelector(
  getEmailActivityState,
  ({ featureRequest }) => featureRequest
);
export const getSummaryCards = createSelector(
  getEmailActivityState,
  ({ summaryCards }) => summaryCards
);
export const getTemplates = createSelector(getEmailActivityState, ({ templates }) => templates);
export const getTrendChart = createSelector(getEmailActivityState, ({ trendChart }) => trendChart);

export const getIsTeamBreakdown = createSelector(
  [isCurrentUserOwner, getEmailActivityScope, getBreakdownView],
  (isOwner, scope, breakdownView) => {
    // Owner-only feature: only owners can flip the view via the toggle and only
    // owners default to By Team in the first place. For everyone else this is false.
    if (!isOwner) {
      return false;
    }
    // Toggle override takes precedence — an owner on Entire-Org scope can pick
    // "By Rep" to group by createdBy across the whole org (no scope filter).
    if (breakdownView !== undefined) {
      return breakdownView === "team";
    }
    // Default behavior: a specific team or user scope forces By Rep; otherwise
    // (Entire-Org for an owner) the view is By Team.
    return !scope.teamId && !scope.userId;
  }
);

export const getShowBreakdownTable = createSelector(
  [isCurrentUserOwner, isCurrentUserManager],
  (isOwner, isManager) => isOwner || isManager
);

// Toggle is owner-only AND only meaningful on Entire-Org scope. A specific team or
// user scope already determines the breakdown grouping (forced By Rep), so exposing
// the switch there would let the user pick a view that the saga ignores.
export const getCanToggleBreakdownView = createSelector(
  [isCurrentUserOwner, getEmailActivityScope],
  (isOwner, scope) => isOwner && !scope.teamId && !scope.userId
);

export const getCurrentScopeTeamName = createSelector(
  [getEmailActivityScope, getTeams],
  (scope, teams) => teams.find((team) => team.id === scope.teamId)?.name
);
